import { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  Paper,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import { useAtomValue } from "jotai";
import { ChatMessage, postChatFeedback } from "../api/manualChat";
import { AppAtoms } from "../lib/store";
import { CONST } from "../config/const";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ManualChatPanelProps {
  pageName?: string;
}

function ManualChatPanel({ pageName }: ManualChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const allSampleQuestions = useAtomValue(AppAtoms.sampleQuestionsAtom);
  const initialQuestions = allSampleQuestions[pageName || 'default'] || allSampleQuestions['default'] || [];
  const [chatLogId, setChatLogId] = useState<number | null>(null);
  const [feedbackSent, setFeedbackSent] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const wsRef = useRef<WebSocket | null>(null);
  const streamingAnswerRef = useRef<string>("");

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  const handleSend = useCallback(
    (overrideMsg?: string) => {
      const msg = overrideMsg ?? inputValue;
      if (!msg.trim()) return;
      setSuggestedQuestions([]);

      const userMsg: ChatMessage = { role: "user", content: msg };

      // 直近3往復の文脈を組み立てる（error定型文を除外して(user,assistant)ペアを最大3組取得）
      const ERROR_ASSISTANT_CONTENTS = [
        "認証情報が取得できません。再ログインしてください。",
        "接続エラーが発生しました。",
      ];
      const filtered = messages.filter(
        (m) =>
          !(
            m.role === "assistant" &&
            (ERROR_ASSISTANT_CONTENTS.includes(m.content) ||
              m.content.startsWith("エラーが発生しました: "))
          )
      );
      const pairsArr: ChatMessage[][] = [];
      for (let i = 0; i < filtered.length - 1; i++) {
        if (filtered[i].role === "user" && filtered[i + 1].role === "assistant") {
          pairsArr.push([filtered[i], filtered[i + 1]]);
          i++;
        }
      }
      const builtHistory = pairsArr.slice(-3).flat();

      setMessages((prev) => [...prev, userMsg]);
      setInputValue("");
      setLoading(true);
      streamingAnswerRef.current = "";

      const idToken = loginUser?.idToken;
      if (!idToken) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "認証情報が取得できません。再ログインしてください。" },
        ]);
        setLoading(false);
        return;
      }

      // WebSocket接続
      const url = CONST.aiChatSupportWebSocketAPIURL + "?Authorization=" + idToken;
      const websocket = new WebSocket(url);
      wsRef.current = websocket;

      websocket.onopen = () => {
        websocket.send(
          JSON.stringify({
            message: msg,
            history: builtHistory,
            page_name: pageName || "",
            user_authority: loginUser?.authority || "",
          })
        );
      };

      websocket.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === "chunk") {
          streamingAnswerRef.current += data.content;
          setMessages((prev) => {
            const updated = [...prev];
            // 最後のメッセージがassistantなら更新、なければ追加
            if (
              updated.length > 0 &&
              updated[updated.length - 1].role === "assistant"
            ) {
              updated[updated.length - 1] = {
                role: "assistant",
                content: streamingAnswerRef.current,
              };
            } else {
              updated.push({
                role: "assistant",
                content: streamingAnswerRef.current,
              });
            }
            return updated;
          });
        } else if (data.type === "done") {
          setLoading(false);
          if (data.suggested_questions && Array.isArray(data.suggested_questions)) {
            setSuggestedQuestions(data.suggested_questions);
          }
          if (data.chat_log_id) {
            setChatLogId(data.chat_log_id);
            setFeedbackSent(false);
          }
          websocket.close();
        } else if (data.type === "error") {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "エラーが発生しました: " + (data.message || "不明なエラー"),
            },
          ]);
          setLoading(false);
          websocket.close();
        }
      };

      websocket.onerror = () => {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "接続エラーが発生しました。" },
        ]);
        setLoading(false);
      };

      websocket.onclose = () => {
        wsRef.current = null;
      };
    },
    [inputValue, loginUser, pageName, messages]
  );

  const handleFeedback = async (feedback: 'good' | 'bad') => {
    if (!chatLogId || feedbackSent) return;
    setFeedbackSent(true);
    try {
      await postChatFeedback(chatLogId, feedback);
    } catch (e) {
      console.error('feedback error', e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.ctrlKey && e.key === "Enter") {
      e.preventDefault();
      if (!loading && inputValue.trim()) {
        handleSend();
      }
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }} data-testid="ai-chat-panel">
      {/* メッセージ領域 — flex:1 で残り全体を使い切る */}
      <Box data-testid="ai-chat-body" sx={{ flex: 1, overflowY: "auto", minHeight: 0, px: 1, py: 0.5 }}>
        {messages.length === 0 ? (
          <Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", mb: 1 }}>
              <SmartToyIcon sx={{ fontSize: 28, color: "text.secondary", mb: 0.5 }} />
              <Paper
                elevation={0}
                sx={{
                  p: 1,
                  mt: 0,
                  bgcolor: "var(--color-washi)",
                  borderRadius: "4px 12px 12px 12px",
                  maxWidth: "85%",
                }}
              >
                <Typography sx={{ fontSize: "13px", whiteSpace: "pre-wrap" }}>
                  {"こんにちは！操作サポートAIです。\n画面の使い方や機能について、\nわからないことがあれば\nお気軽にご質問ください。\n登録方法、CSV取込、設定変更など、\nなんでもお手伝いします。"}
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 1 }}>
              {initialQuestions.map((q) => (
                <Button
                  key={q}
                  variant="outlined"
                  fullWidth
                  sx={{ bgcolor: "white", fontSize: "0.75rem", justifyContent: "flex-start", fontWeight: 400 }}
                  onClick={() => handleSend(q)}
                >
                  {q}
                </Button>
              ))}
            </Box>
          </Box>
        ) : (
          messages.map((msg, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: msg.role === "assistant" ? "flex-start" : "flex-end",
                mb: 1,
              }}
            >
              {msg.role === "assistant" ? (
                <SmartToyIcon sx={{ fontSize: 28, color: "text.secondary", mb: 0.5 }} />
              ) : (
                <PersonIcon sx={{ fontSize: 28, color: "primary.main", mb: 0.5 }} />
              )}
              <Paper
                sx={{
                  display: "inline-block",
                  p: 0.75,
                  maxWidth: "90%",
                  bgcolor: msg.role === "user" ? "var(--color-ai)" : "var(--color-washi)",
                  color: msg.role === "user" ? "var(--color-white)" : "var(--color-sumi)",
                }}
              >
                {msg.role === 'assistant' ? (
                  <Box sx={{
                    fontSize: 'var(--fs-12)',
                    '& p': { margin: '0.25em 0' },
                    '& h1, & h2, & h3, & h4': { margin: '0.5em 0 0.25em', fontSize: '0.8rem', fontWeight: 'bold' },
                    '& ul, & ol': { margin: '0.25em 0', paddingLeft: '1.2em' },
                    '& li': { margin: '0.1em 0' },
                    '& code': { backgroundColor: 'rgba(0,0,0,0.06)', padding: '0.1em 0.3em', borderRadius: '3px', fontSize: '0.65rem' },
                    '& pre': { backgroundColor: 'rgba(0,0,0,0.06)', padding: '0.5em', borderRadius: '4px', overflow: 'auto', margin: '0.25em 0' },
                    '& pre code': { backgroundColor: 'transparent', padding: 0 },
                    '& strong': { fontWeight: 'bold' },
                    '& a': { color: 'primary.main' },
                    '& table': { borderCollapse: 'collapse', margin: '0.25em 0', fontSize: '0.65rem' },
                    '& th, & td': { border: '1px solid #ddd', padding: '0.2em 0.4em' },
                  }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ whiteSpace: "pre-wrap", fontSize: "var(--fs-12)" }}>
                    {msg.content}
                  </Typography>
                )}
              </Paper>
              {/* フィードバックボタン: 最後のAI回答、かつ読み込み完了後のみ表示 */}
              {!loading && chatLogId && i === messages.length - 1 && msg.role === 'assistant' && (
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                  <IconButton
                    data-testid="ai-chat-feedback-good"
                    size="small"
                    onClick={() => handleFeedback('good')}
                    disabled={feedbackSent}
                    color={feedbackSent ? 'default' : 'primary'}
                  >
                    <ThumbUpAltOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    data-testid="ai-chat-feedback-bad"
                    size="small"
                    onClick={() => handleFeedback('bad')}
                    disabled={feedbackSent}
                    color={feedbackSent ? 'default' : 'error'}
                  >
                    <ThumbDownAltOutlinedIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          ))
        )}
        {loading && (
          <Box data-testid="ai-chat-loading" sx={{ display: "flex", justifyContent: "flex-start", mt: 0.5 }}>
            <CircularProgress size={16} />
          </Box>
        )}
        {suggestedQuestions.length > 0 && !loading && messages.length > 0 && (
          <Box data-testid="ai-chat-suggested" sx={{ display: "flex", flexDirection: "column", gap: 0.5, px: 1, mt: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
              関連する質問:
            </Typography>
            {suggestedQuestions.map((q, idx) => (
              <Button
                key={idx}
                size="small"
                sx={{
                  bgcolor: "var(--color-ai-pale)",
                  color: "var(--color-ai)",
                  borderRadius: "999px",
                  fontSize: "11px",
                  px: "10px",
                  py: "3px",
                  minWidth: 0,
                  border: "none",
                  textTransform: "none",
                  justifyContent: "flex-start",
                  "&:hover": { bgcolor: "var(--color-ai-pale)", filter: "brightness(0.95)" },
                }}
                onClick={() => {
                  setSuggestedQuestions([]);
                  handleSend(q);
                }}
              >
                {q}
              </Button>
            ))}
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* 入力欄 — 常にbottom固定 */}
      <Box data-testid="ai-chat-footer" sx={{ borderTop: "1px solid var(--color-border-light)", p: 1.5 }}>
        <TextField
          data-testid="ai-chat-input"
          fullWidth
          size="small"
          multiline
          minRows={1}
          maxRows={4}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="操作方法を質問..."
          disabled={loading}
          sx={{ "& .MuiInputBase-input": { fontSize: "0.75rem" } }}
        />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mt: 0.5, gap: 0.5 }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: "10px" }}>
            Ctrl+Enter で送信
          </Typography>
          <IconButton data-testid="ai-chat-send" size="small" onClick={() => handleSend()} disabled={loading || !inputValue.trim()}>
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

export default ManualChatPanel;
